import { clearSession, setAuthNotice } from "./session";

export const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";
export const NETWORK_ERROR_MESSAGE = "Unable to reach the server. Check your connection and try again.";
export const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Reads the server's message from a JSON or plain-text body without consuming the original response.
const readServerMessage = async (response) => {
  try {
    const text = await response.clone().text();
    if (!text) return "";

    try {
      const data = JSON.parse(text);
      if (typeof data?.message === "string" && data.message.trim()) return data.message;
      if (typeof data?.error === "string" && data.error.trim()) return data.error;
      return "";
    } catch {
      return text.length <= 200 ? text : "";
    }
  } catch {
    return "";
  }
};

const messageForStatus = (status, serverMessage, fallbackMessage) => {
  if (status === 401) return SESSION_EXPIRED_MESSAGE;
  if (status === 403) return serverMessage || "You don't have permission to do this.";
  if (status === 404) return serverMessage || "The requested item could not be found.";
  if (status === 400 || status === 409 || status === 413 || status === 422) {
    return serverMessage || fallbackMessage;
  }
  return fallbackMessage;
};

/**
 * fetch() wrapper with consistent error handling.
 *
 * - Network failures throw an ApiError with a readable message.
 * - 401 clears the stored session and returns the user to login.
 * - Other failures throw an ApiError with a status-specific message.
 * - Pass statuses in `passthrough` to receive that Response and handle it at the call site.
 */
export const apiFetch = async (
  url,
  options = {},
  fallbackMessage = DEFAULT_ERROR_MESSAGE,
  { passthrough = [] } = {},
) => {
  let response;

  try {
    response = await fetch(url, options);
  } catch (error) {
    // Aborts are cancellations, not network failures, so callers that check them keep working.
    if (error?.name === "AbortError") throw error;
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }

  if (response.ok || passthrough.includes(response.status)) {
    return response;
  }

  if (response.status === 401 && !passthrough.includes(401)) {
    clearSession();
    setAuthNotice("warning", SESSION_EXPIRED_MESSAGE);
    window.location.assign("/watchtower");
  }

  const serverMessage = await readServerMessage(response);
  throw new ApiError(messageForStatus(response.status, serverMessage, fallbackMessage), response.status);
};

// Use with .catch() where a failed HTTP response was previously ignored: returns null for HTTP errors.
// Network failures are rethrown so the caller's existing catch still runs.
export const ignoreHttpErrors = (error) => {
  if (error instanceof ApiError && error.status) return null;
  throw error;
};
